import type { Order } from '@talos/kyoko';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { MaxOrderSizeValidationState } from '../../../../hooks/useMaxOrderSizeDialogs';

export interface OrderValidationResult {
  status: MaxOrderSizeValidationState;
}

interface UseBulkResumeValidationStateArgs {
  isOpen: boolean;
  selectedOrders: Order[];
}

export const useBulkResumeValidationState = ({ isOpen, selectedOrders }: UseBulkResumeValidationStateArgs) => {
  const selectedOrderIds = useMemo(() => selectedOrders.map(order => order.OrderID), [selectedOrders]);
  const selectedOrderIdsKey = selectedOrderIds.join('|');
  const selectedOrderIdSet = useMemo(() => new Set(selectedOrderIds), [selectedOrderIds]);
  const openCycleRef = useRef(0);
  const wasOpenRef = useRef(isOpen);

  if (wasOpenRef.current !== isOpen) {
    wasOpenRef.current = isOpen;
    openCycleRef.current += 1;
  }

  const resetKey = `${openCycleRef.current}|${isOpen ? 'open' : 'closed'}|${selectedOrderIdsKey}`;
  const [validationStatusState, setValidationStatusState] = useState<{
    resetKey: string;
    statuses: Map<string, OrderValidationResult>;
  }>({ resetKey, statuses: new Map() });
  const orderValidationStatuses = useMemo(
    () =>
      validationStatusState.resetKey === resetKey
        ? validationStatusState.statuses
        : new Map<string, OrderValidationResult>(),
    [resetKey, validationStatusState]
  );

  const onValidationResult = useCallback(
    (orderId: string, result: OrderValidationResult) => {
      if (!isOpen || !selectedOrderIdSet.has(orderId)) {
        return;
      }
      setValidationStatusState(prev => {
        const statuses = prev.resetKey === resetKey ? prev.statuses : new Map<string, OrderValidationResult>();
        const previousResult = statuses.get(orderId);
        if (previousResult?.status === result.status) {
          return prev;
        }
        return {
          resetKey,
          statuses: new Map(statuses).set(orderId, result),
        };
      });
    },
    [isOpen, resetKey, selectedOrderIdSet]
  );

  const allReported = orderValidationStatuses.size === selectedOrders.length;
  const isValidationPending = !allReported;
  const pendingCount = selectedOrders.length - orderValidationStatuses.size;
  const statuses = [...orderValidationStatuses.values()];
  const hasReject = statuses.some(v => v.status === 'reject');
  const hasWarn = statuses.some(v => v.status === 'warn');
  const unavailableCount = statuses.filter(v => v.status === 'loading').length;
  const hasUnavailableValidation = unavailableCount > 0;
  const showLimitMessage = hasReject || hasWarn || hasUnavailableValidation || isValidationPending;
  const confirmLabel = hasUnavailableValidation
    ? 'Resume without limit check'
    : hasWarn
      ? 'Resume anyway'
      : 'Resume Orders';
  const confirmDisabledForValidation = isValidationPending || hasReject;

  const orderStatuses = useMemo(() => {
    const map = new Map<string, 'warn' | 'reject'>();
    orderValidationStatuses.forEach((result, orderId) => {
      if (result.status === 'warn' || result.status === 'reject') {
        map.set(orderId, result.status);
      }
    });
    return map.size > 0 ? map : undefined;
  }, [orderValidationStatuses]);

  return {
    allReported,
    confirmDisabledForValidation,
    confirmLabel,
    hasReject,
    hasUnavailableValidation,
    hasWarn,
    isValidationPending,
    onValidationResult,
    orderStatuses,
    pendingCount,
    rejectedCount: statuses.filter(v => v.status === 'reject').length,
    showLimitMessage,
    unavailableCount,
    warnCount: statuses.filter(v => v.status === 'warn').length,
  };
};
